import { formatPrice } from "@/src/shared/utils/currency";
import type { IWishlist, IWishlistListing, IWishlistPagination } from "./types";

/** Folder names the backend accepts (`WishlistCreate.name`: 1–100). */
export const WISHLIST_NAME_MAX_LENGTH = 100;
/** Page size of the wishlist tab and a folder's listings, as in the app. */
export const WISHLIST_PAGE_SIZE = 15;
/** Page size used to learn every saved listing at once, as the app's save service does. */
export const WISHLIST_HYDRATE_PAGE_SIZE = 50;

/** The app's fallback when the server gives no message. */
export const WISHLIST_ERROR = "Something went wrong, try again!";
/** Wishlist toasts sit at the bottom like the app's snackbar (the provider default is top-right). */
export const WISHLIST_TOAST = {
  anchorOrigin: { vertical: "bottom", horizontal: "center" },
  autoHideDuration: 3000,
} as const;

/** listing id → ids of the folders it is saved in. */
export type TSavedIn = Record<number, number[]>;

export const wishlistService = {
  /**
   * Whether the heart is filled: the listing is in a folder we know about, or an API
   * said `is_favourite: true` before the folders were loaded.
   */
  isSaved(savedIn: TSavedIn, favourites: number[], listingId: number) {
    return (
      (savedIn[listingId]?.length ?? 0) > 0 || favourites.includes(listingId)
    );
  },

  /** Quick saves land here; null when the user has no default folder. */
  defaultFolder(folders: IWishlist[]): IWishlist | null {
    return folders.find((folder) => folder.is_default) ?? null;
  },

  /** `savedIn` with the listing added to a folder. */
  withSaved(savedIn: TSavedIn, listingId: number, folderId: number): TSavedIn {
    const current = savedIn[listingId] ?? [];
    if (current.includes(folderId)) return savedIn;
    return { ...savedIn, [listingId]: [...current, folderId] };
  },

  /** `savedIn` with the listing taken out of one folder, or all of them when no folder is given. */
  withRemoved(
    savedIn: TSavedIn,
    listingId: number,
    folderId?: number,
  ): TSavedIn {
    const remaining =
      folderId === undefined
        ? []
        : (savedIn[listingId] ?? []).filter((id) => id !== folderId);
    const next = { ...savedIn };
    if (remaining.length) next[listingId] = remaining;
    else delete next[listingId];
    return next;
  },

  /** Builds `savedIn` from every folder's listing ids. */
  savedInFrom(folders: { folderId: number; listingIds: number[] }[]): TSavedIn {
    return folders.reduce<TSavedIn>(
      (savedIn, { folderId, listingIds }) =>
        listingIds.reduce(
          (acc, listingId) =>
            wishlistService.withSaved(acc, listingId, folderId),
          savedIn,
        ),
      {},
    );
  },

  /** The folders after one becomes the default: the server keeps a single default. */
  withDefault(folders: IWishlist[], folderId: number): IWishlist[] {
    return folders.map((folder) => ({
      ...folder,
      is_default: folder.id === folderId,
    }));
  },

  /** The trimmed name, or null when it is empty or too long. */
  validName(name: string): string | null {
    const trimmed = name.trim();
    return trimmed && trimmed.length <= WISHLIST_NAME_MAX_LENGTH
      ? trimmed
      : null;
  },

  /** "3 saved", under a folder's name. */
  savedCount(folder: Pick<IWishlist, "total_count">): string {
    return `${folder.total_count ?? 0} saved`;
  },

  /** The next page to fetch, or undefined after the last one (TanStack stops there). */
  nextPage(pagination: IWishlistPagination | null | undefined) {
    return pagination?.total_pages != null &&
      pagination.page < pagination.total_pages
      ? pagination.page + 1
      : undefined;
  },

  /** Sharing needs a shared folder and the link the server made for it. */
  canShare(folder: Pick<IWishlist, "visibility" | "shared_link">): boolean {
    return folder.visibility === "shared" && !!folder.shared_link;
  },

  /** "BDT 2,500 / Day", or null for a listing without a price. */
  priceLabel(listing: Pick<IWishlistListing, "price" | "showable_price">) {
    const price = listing.price || listing.showable_price || 0;
    return price > 0 ? `${formatPrice(Math.round(price))} / Day` : null;
  },

  /** `{ average: "4.8", count: 12 }`, or null for "New". */
  rating(listing: Pick<IWishlistListing, "ratings">) {
    const { reviews_count = 0, reviews_avg = null } = listing.ratings ?? {};
    return reviews_count > 0 && reviews_avg !== null
      ? { average: reviews_avg.toFixed(1), count: reviews_count }
      : null;
  },
};
